import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import React, { Suspense, useMemo, useState, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Line, Text3D } from '@react-three/drei';
import { gsap } from 'gsap/all';
import { dragState } from './dragState.js';
import { useRenderPath, DEBUG, DIAG } from './device.js';

// ─── Model surface ───────────────────────────────────────────────────────────
// The surface has to match the lighting rig the device actually got, because the
// two rigs put the light in completely different places.
//
//   rectAreaLights — two large soft panels. metalness 0.9 reads as polished
//     gunmetal precisely because the panels are what gets reflected.
//
//   directional only — there is nothing to reflect, and a metal's F0 is its own
//     base colour, so rgb(54,54,54) at metalness 0.9 is a ~5% reflector with
//     essentially no diffuse term either: a black model. The fallback therefore
//     leans on diffuse instead — much lower metalness and a lighter base colour,
//     which cannot come out black no matter what the driver supports.
const SURFACE_AREA_LIGHT = { color: 'rgb(54, 54, 54)', roughness: 0.40, metalness: 0.90 };
// Tuned against a screenshot of the area-light path so the two read alike: dark
// gunmetal with a soft sheen, not a bright silver ball.
const SURFACE_DIRECT     = { color: 'rgb(86, 86, 86)', roughness: 0.50, metalness: 0.45 };

export default function Model() {
    const { rectAreaLights } = useRenderPath();
    const [model, setModel] = useState(null);

    const loader = useMemo(() => {
        const dracoLoader = new DRACOLoader();
        const gltfLoader = new GLTFLoader();
        gltfLoader.setDRACOLoader(dracoLoader);
        return gltfLoader;
    }, []);

    useEffect(() => {
        fetch('./stage.mjs')
            .then(response => response.text())
            .then(text => {
                const modifiedText = 'Z2' + text.substring(2);
                const decryptedArrayBuffer = base64ToArrayBuffer(modifiedText);
                loader.parse(decryptedArrayBuffer, '', function (gltf) {
                    // Apply materials BEFORE first render — eliminates the material flash
                    gltf.scene.traverse((child) => {
                        if (child.isMesh) {
                            child.material = new THREE.MeshStandardMaterial({
                                ...(rectAreaLights ? SURFACE_AREA_LIGHT : SURFACE_DIRECT),
                                side: THREE.DoubleSide,
                            });
                        }
                    });
                    // Center model at world origin so particle field (also at origin) aligns
                    const box = new THREE.Box3().setFromObject(gltf.scene);
                    const center = box.getCenter(new THREE.Vector3());
                    gltf.scene.position.sub(center);
                    // Largest half-extent, which is the radius the model sweeps as it
                    // spins. The camera framing is derived from this rather than a
                    // hard-coded distance, so a narrow phone viewport can be fitted
                    // properly instead of cropping into the model.
                    const extent = box.getSize(new THREE.Vector3());
                    dragState.modelRadius = Math.max(extent.x, extent.y, extent.z) / 2;
                    setModel(gltf.scene);
                    // Signal the overlay to fade out
                    window.dispatchEvent(new Event('model-ready'));
                }, function (error) {
                    console.log(error);
                });
            })
            .catch(error => {
                console.log(error);
            });
        // rectAreaLights is resolved once from the renderer and never changes, so
        // this still runs exactly one fetch — it is in the list because the
        // material choice reads it.
    }, [loader, rectAreaLights]);

    return model ? <ModelContent model={model} /> : null;
}



function ModelContent({ model }) {
    const { raycaster, camera } = useThree();
    const rotationRef = useRef(-(Math.random() * Math.PI * 2));
    const rotXRef = useRef(0);
    const dragMomentumRef = useRef(0);
    const dragMomentumXRef = useRef(0);
    const tiltVelRef   = useRef(0);
    const prevRotXRef  = useRef(0);
    const currentSpeedRef = useRef(0.9);
    const prevHoveringRef = useRef(false); // for detecting hover transitions
    const modelGroupRef = useRef();
    const labelGroupRef = useRef();
    const lineRef = useRef(null);
    const textMatRef = useRef(null);
    const textGroupRef = useRef(null);
    const textLightRef = useRef(null);

    // Press-time hit test, shared with the pointer handlers.
    //
    // The per-frame hover raycast below is driven by the last pointer MOVE, which
    // is fine for a mouse (it always moves before it clicks) but useless for a
    // finger: the first event a touch produces is the press itself, at coordinates
    // the app has never seen. Gating the drag on the stale hover flag meant a
    // one-finger drag never started the model spin at all.
    useEffect(() => {
        dragState.hitTest = (ndcX, ndcY) => {
            if (!modelGroupRef.current) return false;
            raycaster.setFromCamera({ x: ndcX, y: ndcY }, camera);
            return raycaster.intersectObject(modelGroupRef.current, true).length > 0;
        };
        return () => { dragState.hitTest = null; };
    }, [raycaster, camera]);

    // Layer isolation: text light (layer 1) cannot illuminate the model (layer 0)
    useEffect(() => {
        // Light on layer 1 only — won't touch anything on layer 0
        if (textLightRef.current) {
            textLightRef.current.layers.disableAll();
            textLightRef.current.layers.enable(1);
        }
        // Text mesh on layer 0 + 1 — camera sees it (layer 0) and text light hits it (layer 1)
        if (textGroupRef.current) {
            textGroupRef.current.traverse(child => {
                if (child.isMesh) child.layers.enable(1);
            });
        }
    }, []);

    // Pure Three.js rotation — zero React state updates
    useFrame((state, delta) => {
        const safeDelta = Math.min(delta, 1 / 30);

        // ── Hover detection via raycast — runs every frame, no synthetic event timing ────────
        // During drag we freeze hover (pointer captured by div; real position irrelevant).
        // The frame after drag ends, active=false and we immediately get correct hover from raycast.
        if (!dragState.active && modelGroupRef.current) {
            raycaster.setFromCamera({ x: dragState.ndcX, y: dragState.ndcY }, camera);
            dragState.hovering = raycaster.intersectObject(modelGroupRef.current, true).length > 0;
        }

        // The label animates off any CHANGE to the flag, whoever set it — this is
        // deliberately outside the raycast block above. A press sets hovering
        // directly (a finger has no hover before it lands), and if the fade only
        // reacted to the raycast's own result it could never fire on touch: the
        // drag suppresses the raycast for the whole gesture, and the pointerleave
        // that the spec fires after every touch release blanks the pointer position
        // before it resumes.
        if (dragState.hovering !== prevHoveringRef.current) {
            const hit = dragState.hovering;
            prevHoveringRef.current = hit;
            const mats = [lineRef.current?.material, textMatRef.current].filter(Boolean);
            mats.forEach(m => { gsap.killTweensOf(m); gsap.to(m, { opacity: hit ? 1 : 0, duration: 1, ease: 'power2.out' }); });
            if (textLightRef.current) {
                gsap.killTweensOf(textLightRef.current);
                gsap.to(textLightRef.current, { intensity: hit ? 3 : 0, duration: 1, ease: 'power2.out' });
            }
        }

        if (DEBUG) {
            DIAG.label = `${dragState.hovering ? 'hover' : 'idle '} op=${(textMatRef.current?.opacity ?? 0).toFixed(2)}`;
        }

        const isHovering = dragState.hovering;

        // Auto-rotation: decays to 0 on hover-in (slow brake ~3s), ramps up fast on hover-out
        const targetAutoSpeed = isHovering ? 0.0 : 0.3;
        const speedRate = isHovering ? 0.35 : 2.5; // slow brake, snappy spin-up
        currentSpeedRef.current += (targetAutoSpeed - currentSpeedRef.current) * (1 - Math.exp(-safeDelta * speedRate));

        if (dragState.active) {
            dragMomentumRef.current  = dragState.velocity;
            dragMomentumXRef.current = dragState.velocityY;
            dragState.velocity  = 0;
            dragState.velocityY = 0;
        } else {
            // Inertia coast — half-life ~380ms, feels like real spin deceleration
            dragMomentumRef.current  *= Math.exp(-safeDelta * 1.8);
            dragMomentumXRef.current *= Math.exp(-safeDelta * 1.8);
            if (Math.abs(dragMomentumRef.current)  < 0.00005) dragMomentumRef.current  = 0;
            if (Math.abs(dragMomentumXRef.current) < 0.00005) dragMomentumXRef.current = 0;
        }

        // Y rotation: auto-spin is fully suppressed while drag momentum is coasting.
        // Only resume once momentum has fully decayed — prevents opposite-direction fighting.
        if (!dragState.active && dragMomentumRef.current === 0) {
            rotationRef.current -= safeDelta * currentSpeedRef.current;
        }
        rotationRef.current += dragMomentumRef.current;

        // X tilt — accumulate drag, hold while hovering or dragging, spring back on hover-out
        rotXRef.current += dragMomentumXRef.current;
        rotXRef.current = Math.max(-0.5, Math.min(0.5, rotXRef.current));

        const shouldSpringBack = !dragState.active && !isHovering;
        if (shouldSpringBack) {
            const SPRING_K = 7.0;
            const DAMP_K   = 3.2;
            tiltVelRef.current += (-rotXRef.current * SPRING_K - tiltVelRef.current * DAMP_K) * safeDelta;
            rotXRef.current += tiltVelRef.current * safeDelta;
        } else {
            tiltVelRef.current = dragMomentumXRef.current;
        }

        // Blur driven by two independent sources — spin coasting AND tilt spring-back.
        // Use the larger of the two so either motion alone triggers the effect.
        const tiltReturnSpeed = Math.abs(rotXRef.current - prevRotXRef.current);
        prevRotXRef.current   = rotXRef.current;
        const spinBlur      = Math.abs(dragMomentumRef.current);          // 0..~0.04
        const springBlur    = tiltReturnSpeed * 28;                       // tilt delta amplified
        dragState.dragMomentum = Math.max(spinBlur, springBlur);

        if (modelGroupRef.current) {
            modelGroupRef.current.rotation.y = rotationRef.current;
            modelGroupRef.current.rotation.x = rotXRef.current;
        }
        if (labelGroupRef.current) {
            labelGroupRef.current.rotation.y = rotationRef.current;
            labelGroupRef.current.rotation.x = rotXRef.current;
        }
        // Expose both rotation angles so particle trails can follow exactly
        dragState.rotY = rotationRef.current;
        dragState.rotX = rotXRef.current;
        dragState.modelReady = true; // signal swarm that rotY is valid
    });

    return (
        <>
            <group ref={modelGroupRef}>
                <primitive object={model} />
            </group>

            <group ref={labelGroupRef}>
                {/* Dedicated light that fades in with the text — always illuminates it */}
                {/* Light positioned above and slightly in front of the text at ~35° — shines directly at the label face */}
                <pointLight ref={textLightRef} position={[1.4, 0.4, 2.2]} intensity={0} distance={5} color="#ffffff" />
                <Line ref={lineRef}
                    points={[new THREE.Vector3(0, 0, 0), new THREE.Vector3(1, -0.7, 0.7)]}
                    color={"rgb(61, 61, 61)"}
                    lineWidth={0.5}
                    opacity={0}
                    transparent={true}
                />
                <group ref={textGroupRef}>
                <Text3D
                    font="./fonts/helvetiker_regular.typeface.json"
                    size={0.1}
                    position={[1, -0.7, 0.7]}
                    height={0.016}
                >
                    rv@designova.cz
                    <meshStandardMaterial
                        ref={textMatRef}
                        attach="material"
                        color="rgb(81, 81, 81)"
                        roughness={0.3}
                        metalness={0.4}
                        opacity={0}
                        transparent={true}
                    />
                </Text3D>
                </group>
            </group>
        </>
    );
}

function base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
}
